import React, { useState } from "react"
import { asDialogData } from "../../../core/utils/utils"
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, IconButton, Paper, Stack, TextField, Typography
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import AddIcon from "@mui/icons-material/Add"
import {
    useGetAllExperienceCategoriesQuery,
    useGetActiveSeasonQuery,
    useCreateExperienceCategoryMutation,
    useUpdateExperienceCategoryMutation,
    useDeleteExperienceCategoryMutation,
} from "../../../core/api/experience-api"
import ResponseDialog from "../../ResponseDialog"

export default function ExperienceCategoriesAdmin() {
    const { data: categories = [], isLoading } = useGetAllExperienceCategoriesQuery()
    const { data: activeSeason } = useGetActiveSeasonQuery()
    const [createCat, createResp] = useCreateExperienceCategoryMutation()
    const [updateCat, updateResp] = useUpdateExperienceCategoryMutation()
    const [deleteCat, deleteResp] = useDeleteExperienceCategoryMutation()

    const [editing, setEditing] = useState(null)
    const [form, setForm] = useState({ name: "", description: "", color: "#F05125", active: true, season: "" })
    const [respOpen, setRespOpen] = useState(false)
    const [actionResp, setActionResp] = useState({})

    function openNew() {
        setEditing({})
        setForm({ name: "", description: "", color: "#F05125", active: true, season: activeSeason?.number ?? "" })
    }
    function openEdit(row) {
        setEditing(row)
        setForm({ name: row.name, description: row.description || "", color: row.color || "#F05125", active: row.active, season: row.season ?? "" })
    }
    function close() { setEditing(null) }

    async function save() {
        const { season, ...body } = form
        // Сезон задаётся только при создании; пустое поле → активный сезон.
        if (!editing.id && season !== "") {
            body.season = parseInt(season, 10)
        }
        const resp = editing.id
            ? await updateCat({ id: editing.id, ...body })
            : await createCat(body)
        setActionResp(asDialogData(resp)); setRespOpen(true); close()
    }
    async function remove(row) {
        if (!window.confirm(`Delete category «${row.name}»?`)) return
        const resp = await deleteCat(row.id)
        setActionResp(asDialogData(resp)); setRespOpen(true)
    }

    const columns = [
        { field: "season", headerName: "Season", width: 90, renderCell: (p) => p.value ?? "—" },
        { field: "name", headerName: "Name", width: 180 },
        { field: "description", headerName: "Description", flex: 1 },
        { field: "color", headerName: "Color", width: 100, renderCell: (p) =>
            <Box sx={{ width: 24, height: 24, borderRadius: "50%", bgcolor: p.value || "#ccc", border: "1px solid #eee" }} />
        },
        { field: "active", headerName: "Status", width: 110, renderCell: (p) =>
            <Chip size="small" label={p.value ? "active" : "inactive"} color={p.value ? "success" : "default"} />
        },
        { field: "actions", headerName: "", width: 100, renderCell: (p) =>
            <Stack direction="row">
                <IconButton size="small" onClick={() => openEdit(p.row)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => remove(p.row)}><DeleteIcon fontSize="small" /></IconButton>
            </Stack>
        },
    ]

    if (isLoading) return <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>

    return (
        <Box sx={{ py: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">XP categories</Typography>
                <Button variant="contained" color="warning" startIcon={<AddIcon />} onClick={openNew}>
                    New category
                </Button>
            </Stack>
            <Paper sx={{ height: 560 }}>
                <DataGrid
                    rows={categories}
                    columns={columns}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                />
            </Paper>

            <Dialog open={!!editing} onClose={close} maxWidth="sm" fullWidth>
                <DialogTitle>{editing?.id ? "Edit category" : "New category"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                        <TextField label="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} multiline minRows={2} />
                        <TextField label="Color (hex)" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} />
                        <TextField
                            label="Season"
                            type="number"
                            helperText={editing?.id ? "Сезон записи менять нельзя (categoryXp ключуется по имени в сезоне)" : "Пусто — активный сезон; будущий номер — подготовка контента заранее"}
                            value={form.season}
                            onChange={e => setForm({ ...form, season: e.target.value })}
                            disabled={!!editing?.id}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={save} disabled={createResp.isLoading || updateResp.isLoading}>Save</Button>
                </DialogActions>
            </Dialog>

            <ResponseDialog
                open={respOpen}
                onClose={() => setRespOpen(false)}
                responseData={actionResp}
                label="Category"
                information="Saved"
            />
        </Box>
    )
}
